import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useAuthV2 } from '../../hooks/useAuthV2';

// STD-015: server state for the AdminTools domain. apiService is imported here
// (a *.queries.ts file) per STD-016. Errors are toasted by the global QueryCache
// handler (lib/queryClient); meta.errorMessage supplies the i18n fallback.

export const adminToolsKeys = {
  all: ['adminTools'] as const,
  organizations: () => [...adminToolsKeys.all, 'organizations'] as const,
  unassignedUsers: () => [...adminToolsKeys.all, 'unassigned-users'] as const,
  currentExtractionJob: () => [...adminToolsKeys.all, 'current-extraction-job'] as const,
  extractionJobStatus: (jobId: string | null) =>
    [...adminToolsKeys.all, 'extraction-job-status', jobId] as const,
  organizationUsers: (orgId: string | null) =>
    [...adminToolsKeys.all, 'organization-users', orgId] as const,
  announcementsAdmin: () => [...adminToolsKeys.all, 'announcements-admin'] as const,
  // Current user's organization users. The server scopes the result to the
  // caller's org via JWT, but the cache key must carry the org id so a different
  // org's list can't be served from cache after an in-session org switch.
  myOrganizationUsers: (orgId?: string) =>
    orgId
      ? ([...adminToolsKeys.all, 'my-organization-users', orgId] as const)
      : ([...adminToolsKeys.all, 'my-organization-users'] as const),
  dronesWithOrganizations: () => [...adminToolsKeys.all, 'drones-with-organizations'] as const,
  dronesLegacyHours: () => [...adminToolsKeys.all, 'drones-legacy-hours'] as const,
  pilotsLegacyHours: () => [...adminToolsKeys.all, 'pilots-legacy-hours'] as const,
  maintenanceDocuments: () => [...adminToolsKeys.all, 'maintenance-documents'] as const,
  serialNumbers: () => [...adminToolsKeys.all, 'serial-numbers'] as const,
  tenderEmails: (status: string) => [...adminToolsKeys.all, 'tender-emails', status] as const,
};

// All organizations — used in org management and announcement targeting dialogs.
// Pass enabled=false for non-XerAdmin users (backend returns 403 for them).
export function useAllOrganizationsQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.organizations(),
    queryFn: () => apiService.getAllOrganizations(),
    enabled,
  });
}

// Unassigned users — users who registered but haven't been assigned to an org.
// Pass enabled=false for non-XerAdmin users (backend returns 403 for them).
export function useUnassignedUsersQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.unassignedUsers(),
    queryFn: () => apiService.getUnassignedUsers(),
    enabled,
  });
}

// Current extraction job — checked on page load to resume any in-progress job.
// Global error toast is suppressed: this is a background check; 404/null is fine.
// Pass enabled=false for non-XerAdmin users — the extraction feature is XerAdmin-only.
export function useCurrentExtractionJobQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.currentExtractionJob(),
    queryFn: () => apiService.getCurrentExtractionJob(),
    enabled,
    meta: { skipGlobalErrorToast: true },
  });
}

// Extraction job status — polled while a job is running.
// refetchInterval is controlled by the caller (5000 ms while running, false when done).
// Global error toast is suppressed: polling errors are shown inline.
export function useExtractionJobStatusQuery(
  jobId: string | null,
  refetchInterval?: number | false
) {
  return useQuery({
    queryKey: adminToolsKeys.extractionJobStatus(jobId),
    queryFn: () => apiService.getExtractionJobStatus(jobId!),
    enabled: jobId != null,
    refetchInterval,
    meta: { skipGlobalErrorToast: true },
  });
}

// Users belonging to a specific organization — fetched when an org row is expanded.
export function useOrganizationUsersQuery(orgId: string | null) {
  return useQuery({
    queryKey: adminToolsKeys.organizationUsers(orgId),
    queryFn: () => apiService.getOrganizationUsersById(orgId!),
    enabled: orgId != null,
  });
}

// Raw async fetch for a single org's users — used with queryClient.fetchQuery
// in contexts where multiple orgs must be fetched in a loop (e.g. announcement targeting).
export function fetchOrganizationUsers(orgId: string) {
  return apiService.getOrganizationUsersById(orgId);
}

// All announcements (admin view) — shown in the Announcements dialog.
// Pass enabled=false for non-XerAdmin users (backend returns 403 for them).
export function useAllAnnouncementsAdminQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.announcementsAdmin(),
    queryFn: () => apiService.getAllAnnouncementsAdmin(),
    enabled,
  });
}

// Users in the caller's own organization — fetched when the User Management dialog opens.
// Backend scopes the result to the caller's org, so no orgId argument is needed.
export function useMyOrganizationUsersQuery(enabled = true) {
  const { profile } = useAuthV2();
  return useQuery({
    queryKey: adminToolsKeys.myOrganizationUsers(profile?.organization?.id),
    queryFn: () => apiService.getOrganizationUsers(),
    enabled,
    meta: { errorMessage: 'Failed to fetch users' },
  });
}

// All drones with their organization assignment — fetched when the Drone Assignments dialog opens.
export function useDronesWithOrganizationsQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.dronesWithOrganizations(),
    queryFn: () => apiService.getAllDronesWithOrganizations(),
    enabled,
    meta: { errorMessage: 'Failed to fetch drones' },
  });
}

// Drones with legacy hours — fetched when the Legacy Hours Migration dialog opens.
export function useDronesLegacyHoursQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.dronesLegacyHours(),
    queryFn: () => apiService.getDronesLegacyHours(),
    enabled,
    meta: { errorMessage: 'Failed to fetch legacy hours data' },
  });
}

// Pilots with legacy flight hours — fetched when the Legacy Hours Migration dialog opens.
export function usePilotsLegacyHoursQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.pilotsLegacyHours(),
    queryFn: () => apiService.getPilotsLegacyHours(),
    enabled,
    meta: { errorMessage: 'Failed to fetch legacy hours data' },
  });
}

// Maintenance documents list — fetched when the Maintenance Documents dialog opens.
export function useMaintenanceDocumentsQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.maintenanceDocuments(),
    queryFn: () => apiService.listMaintenanceDocuments(),
    enabled,
    meta: { errorMessage: 'Failed to fetch maintenance documents' },
  });
}

// Drone serial numbers — fetched when the Maintenance Recalculate dialog opens.
export function useSerialNumbersQuery(enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.serialNumbers(),
    queryFn: () => apiService.getSerialNumbers(),
    enabled,
    meta: { errorMessage: 'Failed to load drone list' },
  });
}

// Tender emails (optionally filtered by status) — fetched when the Tender Emails dialog opens.
// statusFilter '' means "all"; it is part of the query key so changing the filter refetches.
export function useTenderEmailsQuery(statusFilter: string, enabled = true) {
  return useQuery({
    queryKey: adminToolsKeys.tenderEmails(statusFilter),
    queryFn: () => apiService.getTenderEmails(statusFilter || undefined, 100),
    enabled,
    meta: { errorMessage: 'Failed to fetch tender emails' },
  });
}
